import { useImpersonation } from '@shared/contexts/ImpersonationContext';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Feature 695: Format relative time (e.g., "5 minutes ago")
function formatRelativeTime(
  startTime: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const start = new Date(startTime);
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return t('time.startedJustNow');
  } else if (diffMinutes < 60) {
    return t('time.startedMinutesAgo', { count: diffMinutes });
  } else if (diffHours < 24) {
    return t('time.startedHoursAgo', { count: diffHours });
  } else {
    return t('time.startedDaysAgo', { count: diffDays });
  }
}

// Feature 695: Format absolute time for hover tooltip (e.g., "Started at 2:30 PM")
function formatAbsoluteTime(
  startTime: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
  locale: string
): string {
  const start = new Date(startTime);
  const timeStr = start.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const dateStr = start.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  });
  return t('time.startedAt', { time: timeStr, date: dateStr });
}

export default function ImpersonationBanner() {
  const { t, i18n } = useTranslation('common');
  const { isImpersonating, impersonatedUserInfo } = useImpersonation();
  const location = useLocation();
  const [relativeTime, setRelativeTime] = useState<string>('');
  const [isDismissed, setIsDismissed] = useState(false);

  const { name, email, organization, adminInfo, startedAt } = impersonatedUserInfo || {};

  const orgName = organization?.name;
  const adminName = adminInfo?.fullName;
  const adminEmail = adminInfo?.email;

  // Feature 696: Reset dismiss state when route changes
  useEffect(() => {
    const currentRoute = location.pathname + location.search;
    const dismissedRoute = sessionStorage.getItem('impersonation_banner_dismissed_route');

    // If banner was dismissed on a different route, show it again
    if (dismissedRoute !== currentRoute) {
      setIsDismissed(false);
    } else if (dismissedRoute === currentRoute) {
      setIsDismissed(true);
    }
  }, [location]);

  // Feature 695: Update relative time every minute
  useEffect(() => {
    if (!startedAt) return;

    // Initial update
    setRelativeTime(formatRelativeTime(startedAt, t));

    // Update every minute
    const interval = setInterval(() => {
      setRelativeTime(formatRelativeTime(startedAt, t));
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [startedAt, t]);

  const absoluteTime = startedAt
    ? formatAbsoluteTime(startedAt, t, i18n.resolvedLanguage || i18n.language)
    : '';

  if (!isImpersonating || !impersonatedUserInfo || isDismissed) {
    return null;
  }

  return (
    <div
      id="impersonation-banner"
      className="bg-state-warn-light border-b-4 border-black/40 shadow-md dark:bg-state-warn-dark"
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
        {/* Mobile: Stacked layout */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          {/* Icon and Message */}
          <div className="flex items-start sm:items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
            {/* Icon */}
            <div className="flex-shrink-0 pt-0.5 sm:pt-0">
              <svg
                className="h-5 w-5 text-white dark:text-on-accent-dark"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Message - with truncation on mobile */}
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-white truncate dark:text-on-accent-dark">
                {t('impersonation.banner.actingAs')} <span className="font-semibold">{name}</span>{' '}
                <span className="hidden sm:inline">({email})</span>
                <span
                  className="sm:hidden inline-block max-w-[120px] align-middle overflow-hidden text-ellipsis whitespace-nowrap"
                  title={email}
                >
                  ({email})
                </span>
                {orgName && (
                  <>
                    {' - '}
                    <span
                      className="font-semibold truncate inline-block max-w-[150px] sm:max-w-none align-middle overflow-hidden text-ellipsis whitespace-nowrap"
                      title={orgName}
                    >
                      {orgName}
                    </span>
                  </>
                )}
                {adminName && (
                  <>
                    {' | '}
                    <span className="font-semibold">
                      {t('impersonation.banner.adminPrefix')} {adminName}
                    </span>
                    {adminEmail && <span className="hidden sm:inline"> ({adminEmail})</span>}
                  </>
                )}
              </p>
              <p className="text-[10px] sm:text-xs text-white mt-0.5 line-clamp-2 sm:line-clamp-none dark:text-on-accent-dark">
                {t('impersonation.banner.actionNotice', {
                  action: t('impersonation.stopImpersonating'),
                })}
                {relativeTime && (
                  <>
                    {' '}
                    <span className="font-medium" title={absoluteTime} style={{ cursor: 'help' }}>
                      {relativeTime}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Close button - larger touch target on mobile (44px minimum) */}
          <div className="flex-shrink-0 sm:ml-4 self-end sm:self-auto">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md bg-black/15 p-1.5 text-white hover:bg-black/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-state-warn-light sm:h-auto sm:w-auto h-11 w-11 dark:text-on-accent-dark dark:hover:bg-black/20 dark:focus-visible:ring-on-accent-dark dark:focus-visible:ring-offset-state-warn-dark"
              onClick={() => {
                // Feature 696: Dismiss banner for current route
                const currentRoute = location.pathname + location.search;
                sessionStorage.setItem('impersonation_banner_dismissed_route', currentRoute);
                setIsDismissed(true);
              }}
            >
              <span className="sr-only">{t('actions.dismiss')}</span>
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
